import {
  ARTICLE_SEARCH_RESULTS_PER_PAGE,
  MAX_ARTICLE_SEARCH_TERM_LENGTH,
} from 'constants/articleResults';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import {
  getArticleCategoriesInMenu,
  getArticlesByCategories,
  getContentHubHomePageData,
  getLatestArticles,
  getPagedArticleSearchResults,
} from 'services/cmsService';
import { IArticleCategorySummary, IArticleSummary, ICategorisedArticles } from 'types/cms';
import { buildArticleItemListJsonLd } from 'utils/articleItemListSchema';
import { tryToNoticeError } from 'utils/errorInfo';
import { stringifyJsonLd } from 'utils/jsonLd';
import { parsePageQueryParam } from 'utils/parsePageQueryParam';

import Typography from '@material-ui/core/Typography';

import ArticleListSection from 'components/CMS/ArticleListSection';
import ArticlePagination from 'components/CMS/ArticlePagination';
import ArticleSearchBox from 'components/CMS/ArticleSearchBox';
import {
  FEATURED_ARTICLES_DISPLAY_LIMIT,
  FeaturedArticleSection,
} from 'components/CMS/FeaturedArticleSection';

import { ContentHubContainer } from 'modules/ContentHubContainer';

import styles from './index.module.scss';

interface IProps {
  categoriesOnMenu: IArticleCategorySummary[];
  latestArticles?: IArticleSummary[];
  articlesWithCategories?: ICategorisedArticles[];
  metaTitle?: string;
  metaDescription?: string;
  index?: boolean;
  searchTerm?: string;
  searchResults?: IArticleSummary[];
  searchTotal?: number;
  searchPage?: number;
}

const Articles = ({
  categoriesOnMenu,
  latestArticles = [],
  articlesWithCategories = [],
  metaTitle,
  metaDescription,
  index = true,
  searchTerm = '',
  searchResults = [],
  searchTotal = 0,
  searchPage = 1,
}: IProps) => {
  const router = useRouter();
  const isSearching = !!searchTerm;
  const hasSearchResults = searchResults.length > 0;
  const searchPageCount = Math.max(Math.ceil(searchTotal / ARTICLE_SEARCH_RESULTS_PER_PAGE), 1);

  const ifShowFirstPart = (l, i) => i === 0 || (i === 1 && l > 2);

  const itemListJsonLd = buildArticleItemListJsonLd([
    // FeaturedArticleSection only renders the first FEATURED_ARTICLES_DISPLAY_LIMIT of these -
    // capping here keeps every ListItem pointing at a URL that's actually on the page.
    ...latestArticles.slice(0, FEATURED_ARTICLES_DISPLAY_LIMIT),
    ...articlesWithCategories.flatMap(item => item.data),
  ]);

  const handleSearch = (term: string) => {
    if (!term) {
      // Nothing to undo if we're not already showing search results - avoids a pointless SSR
      // round-trip to the URL we're already on.
      if (isSearching) {
        router.push({ pathname: '/articles' });
      }
      return;
    }
    router.push({
      pathname: '/articles',
      query: { q: term.slice(0, MAX_ARTICLE_SEARCH_TERM_LENGTH) },
    });
  };

  const handleSearchPageChange = (page: number) => {
    router.push({ pathname: '/articles', query: { q: searchTerm, page } });
  };

  return (
    <ContentHubContainer categories={categoriesOnMenu}>
      {!isSearching && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: stringifyJsonLd(itemListJsonLd) }}
          />
        </Head>
      )}
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        noindex={isSearching || !index}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          type: 'article',
        }}
      />
      <ArticleSearchBox initialSearchTerm={searchTerm} onSearch={handleSearch} />
      {isSearching ? (
        <>
          <Typography
            variant="h5"
            component="h1"
            className={styles.searchResultsTitle}
            data-testid="article-search-results-heading"
          >
            {hasSearchResults
              ? `Search results for "${searchTerm}"`
              : `No articles found for "${searchTerm}"`}
          </Typography>
          {hasSearchResults ? (
            <ArticleListSection articles={searchResults} />
          ) : (
            <div className={styles.emptyState} data-testid="article-search-empty-state">
              <Typography variant="body1">
                We couldn&apos;t find any articles matching your search. Try a different keyword.
              </Typography>
            </div>
          )}
          <ArticlePagination
            pageCount={searchPageCount}
            page={searchPage}
            onChange={handleSearchPageChange}
          />
        </>
      ) : (
        <>
          <FeaturedArticleSection articles={latestArticles} />
          {articlesWithCategories.map(
            (item, index) =>
              ifShowFirstPart(articlesWithCategories.length, index) && (
                <ArticleListSection
                  key={item.name}
                  articleListTitle={item.name}
                  articles={item.data}
                />
              ),
          )}
          {articlesWithCategories.map(
            (item, index) =>
              !ifShowFirstPart(articlesWithCategories.length, index) && (
                <ArticleListSection
                  key={item.name}
                  articleListTitle={item.name}
                  articles={item.data}
                />
              ),
          )}
        </>
      )}
    </ContentHubContainer>
  );
};

function parseSearchTermQueryParam(q: string | string[] | undefined): string {
  const raw = Array.isArray(q) ? q[0] : q;
  return typeof raw === 'string' ? raw.trim() : '';
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<IProps>> {
  let homePageData;
  let categoriesOnMenu: IArticleCategorySummary[];

  try {
    [homePageData, categoriesOnMenu] = await Promise.all([
      getContentHubHomePageData(),
      getArticleCategoriesInMenu(),
    ]);
  } catch (error) {
    const errorMessage = `Article Landing Page Data Error: ${error.message}`;
    tryToNoticeError(new Error(errorMessage, { cause: error }), { error });
    return { notFound: true };
  }

  // Checked before branching so a CMS-configured redirect for /articles is honoured on every
  // request, including search ones (?q=...) - not just the plain landing page.
  if (homePageData.redirect_url) {
    return {
      redirect: {
        destination: homePageData.redirect_url,
        permanent: true,
      },
    };
  }

  const searchTerm = parseSearchTermQueryParam(ctx.query.q);

  if (searchTerm) {
    try {
      const requestedPage = parsePageQueryParam(ctx.query.page) ?? 1;
      const { articles, total } = await getPagedArticleSearchResults(
        searchTerm,
        ARTICLE_SEARCH_RESULTS_PER_PAGE,
        (requestedPage - 1) * ARTICLE_SEARCH_RESULTS_PER_PAGE,
      );
      const searchPageCount = Math.max(Math.ceil(total / ARTICLE_SEARCH_RESULTS_PER_PAGE), 1);

      if (total > 0 && requestedPage > searchPageCount) {
        return {
          redirect: {
            destination: `/articles?${new URLSearchParams({
              q: searchTerm,
              page: String(searchPageCount),
            }).toString()}`,
            permanent: false,
          },
        };
      }

      return {
        props: {
          categoriesOnMenu,
          searchTerm,
          searchResults: articles,
          searchTotal: total,
          searchPage: requestedPage,
          metaTitle: `Search results for "${searchTerm}"`,
          metaDescription: `Articles matching "${searchTerm}"`,
        },
      };
    } catch (error) {
      const errorMessage = `Article Search Data Error: ${error.message}`;
      tryToNoticeError(new Error(errorMessage, { cause: error }), { error });
      return { notFound: true };
    }
  }

  try {
    const [latestArticles, articlesWithCategories] = await Promise.all([
      getLatestArticles(),
      getArticlesByCategories(categoriesOnMenu),
    ]);

    return {
      props: {
        categoriesOnMenu,
        latestArticles,
        articlesWithCategories,
        metaTitle: homePageData.meta_title,
        metaDescription: homePageData.meta_description,
        index: homePageData.index ?? true,
      },
    };
  } catch (error) {
    const errorMessage = `Article Landing Page Data Error: ${error.message}`;
    tryToNoticeError(new Error(errorMessage, { cause: error }), { error });
    return { notFound: true };
  }
}

export default Articles;
